import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { Toaster } from "sonner";
import Lenis from "lenis";

import HomePage from "@/pages/HomePage";
import KoncertyPage from "@/pages/KoncertyPage";
import MerchPage from "@/pages/MerchPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AboutPage from "@/pages/AboutPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderSuccessPage from "@/pages/OrderSuccessPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
// Admin – kód-split: admin bundle se stáhne až po prvním přechodu na /admin
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminEvents = lazy(() => import("@/pages/admin/AdminEvents"));
const AdminPosts = lazy(() => import("@/pages/admin/AdminPosts"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminFanPhotos = lazy(() => import("@/pages/admin/AdminFanPhotos"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminNewsletter = lazy(() => import("@/pages/admin/AdminNewsletter"));
const AdminDiagnostics = lazy(() => import("@/pages/admin/AdminDiagnostics"));
const AdminSms = lazy(() => import("@/pages/admin/AdminSms"));
const AdminCms = lazy(() => import("@/pages/admin/AdminCms"));
import { TermsPage, GdprPage, ShippingPage } from "@/pages/LegalPages";
import NotFoundPage from "@/pages/NotFoundPage";
import ScrollToTop from "@/components/ScrollToTop";
import VisitTracker from "@/components/VisitTracker";

import "@/App.css";

const AdminFallback = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: "#050505" }}>
    <div className="text-white" style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.75rem", letterSpacing: "0.2em" }}>
      NAČÍTÁNÍ ADMINU…
    </div>
  </div>
);

function ProtectedRoute({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#050505" }}>
      <div className="text-white" style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.75rem", letterSpacing: "0.2em" }}>
        NAČÍTÁNÍ...
      </div>
    </div>
  );
  if (!admin) return <Navigate to="/admin" replace />;
  return children;
}

function AppContent() {
  useEffect(() => {
    // Respect user preference + skip Lenis on small/slow devices for smoother UX on phones.
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isSmallScreen = window.matchMedia("(max-width: 768px)").matches;
    // hardwareConcurrency is a decent proxy for old/slow phones (<=2 cores typically a low-end phone)
    const lowCores = (navigator.hardwareConcurrency || 8) <= 2;
    const skipLenis = prefersReducedMotion || (isSmallScreen && lowCores);

    let lenis;
    if (!skipLenis) {
      lenis = new Lenis({ duration: 1.2, easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true });
      window.__lenis = lenis;
      function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
      requestAnimationFrame(raf);
    }

    // Remove Emergent platform badge ONLY (not legitimate links pointing to emergentagent.com preview URLs)
    const removeBadge = () => {
      document.querySelectorAll('a, div, span').forEach(el => {
        const href = el.getAttribute('href') || '';
        const txt = el.textContent?.trim() || '';
        const isBadgeLink = /^https?:\/\/(www\.)?emergent\.sh/i.test(href) || href === 'https://app.emergent.sh';
        const isBadgeText = txt === 'Made with Emergent';
        if (isBadgeLink || isBadgeText) {
          el.closest('[style*="position: fixed"]')?.remove() || el.remove();
        }
      });
    };
    const timer = setTimeout(removeBadge, 800);
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => { if (lenis) lenis.destroy(); clearTimeout(timer); observer.disconnect(); };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <VisitTracker />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/koncerty" element={<KoncertyPage />} />
        <Route path="/merch" element={<MerchPage />} />
        <Route path="/merch/:id" element={<ProductDetailPage />} />
        <Route path="/aktuality" element={<BlogPage />} />
        <Route path="/aktuality/:slug" element={<BlogPostPage />} />
        <Route path="/o-nas" element={<AboutPage />} />
        <Route path="/kosik" element={<CartPage />} />
        <Route path="/pokladna" element={<CheckoutPage />} />
        <Route path="/objednavka-prijata" element={<OrderSuccessPage />} />
        <Route path="/objednavka-prijata/:orderNumber" element={<OrderSuccessPage />} />
        <Route path="/obchodni-podminky" element={<TermsPage />} />
        <Route path="/gdpr" element={<GdprPage />} />
        <Route path="/doruceni" element={<ShippingPage />} />
        <Route path="/admin" element={
          <Suspense fallback={<AdminFallback />}>
            <AdminLogin />
          </Suspense>
        } />
        <Route path="/admin/*" element={
          <ProtectedRoute>
            <Suspense fallback={<AdminFallback />}>
              <AdminLayout>
                <Routes>
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="events" element={<AdminEvents />} />
                  <Route path="posts" element={<AdminPosts />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="content" element={<AdminContent />} />
                  <Route path="fan-photos" element={<AdminFanPhotos />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="newsletter" element={<AdminNewsletter />} />
                  <Route path="diagnostics" element={<AdminDiagnostics />} />
                  <Route path="sms" element={<AdminSms />} />
                  <Route path="cms" element={<AdminCms />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="" element={<Navigate to="dashboard" replace />} />
                </Routes>
              </AdminLayout>
            </Suspense>
          </ProtectedRoute>
        } />
        {/* 404 catch-all – auto redirects to home after 5s */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <div className="App">
              <AppContent />
              <Toaster
                position="top-center"
                richColors
                theme="dark"
                toastOptions={{
                  style: {
                    background: "#0A0A0A",
                    border: "1px solid rgba(255,0,90,0.4)",
                    color: "#FFFFFF",
                    fontFamily: "Manrope, sans-serif",
                  },
                }}
              />
            </div>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
